/**
 * The web half of `siteStorage` (ADR-0104, ADR-0106): a cookie keyed to the site, not the origin.
 *
 * `localStorage` partitions by origin, so a Pull Sheet built on the browse host is unreadable from
 * the Show page on the events host. A cookie set for the domain both hosts share is readable from
 * either. Only small opaque ids live here, and a cross-origin `fetch` sends none of them.
 */

// The Pull Sheet entries' lifetime, not every caller's: long enough for a multi-day Show, short
// enough that the index cannot grow towards a cookie's 4KB limit. `siteCookie` callers pass their
// own lifetime, so a shorter-lived entry is never stored for longer.
const PULL_SHEET_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;
const PROBE_KEY = "trovie.site-probe";

/**
 * The domain the public hosts share, taken as the last two labels. Undefined where there is
 * nothing to share (a single-label host, an IP), which leaves a host-only cookie.
 */
export function siteCookieDomain(hostname: string): string | undefined {
  if (/^[\d.]+$/.test(hostname)) return undefined;
  const labels = hostname.split(".");
  return labels.length > 2 ? labels.slice(-2).join(".") : undefined;
}

function readCookie(key: string): string | null {
  const prefix = `${key}=`;
  const hit = document.cookie.split(/;\s*/).find((entry) => entry.startsWith(prefix));
  return hit === undefined ? null : decodeURIComponent(hit.slice(prefix.length));
}

function attributes(domain: string | undefined, maxAge: number): string {
  return [
    domain && `domain=${domain}`,
    "path=/",
    `max-age=${maxAge}`,
    "SameSite=Lax",
    window.location.protocol === "https:" && "Secure",
  ].filter(Boolean).join(";");
}

let resolved: { domain: string | undefined; } | undefined;

/**
 * Returns the shared cookie domain if the browser accepts it. A browser silently drops a cookie set
 * for a public suffix, and the hostname alone cannot say which suffixes are public, so the
 * candidate is written once and read back.
 */
function sharedDomain(): string | undefined {
  if (!resolved) {
    const candidate = siteCookieDomain(window.location.hostname);
    let accepted = false;
    if (candidate) {
      const token = String(Date.now());
      document.cookie = `${PROBE_KEY}=${token};${attributes(candidate, 60)}`;
      accepted = readCookie(PROBE_KEY) === token;
      document.cookie = `${PROBE_KEY}=;${attributes(candidate, 0)}`;
    }
    resolved = { domain: accepted ? candidate : undefined };
  }
  return resolved.domain;
}

/**
 * Expire any host-only copy of the key. Two cookies of one name are both readable and the browser
 * decides which comes first, so a value written before the shared domain resolved could otherwise
 * shadow the one written after it.
 */
function clearHostOnly(key: string): void {
  if (sharedDomain()) document.cookie = `${key}=;${attributes(undefined, 0)}`;
}

/**
 * The cookie itself: synchronous, with the lifetime named at each write (ADR-0106).
 *
 * `siteStorage` below only wraps this in promises to match native AsyncStorage. The Show identity
 * reads, decides and writes inside one synchronous function, so it takes this instead and has no
 * `await` in the middle of a decision. Every method throws where a browser refuses cookies; the
 * caller absorbs that.
 */
export const siteCookie = {
  read: (key: string): string | null => readCookie(key),
  write: (key: string, value: string, maxAgeSeconds: number): void => {
    clearHostOnly(key);
    document.cookie = `${key}=${encodeURIComponent(value)};${
      attributes(sharedDomain(), maxAgeSeconds)
    }`;
  },
  clear: (key: string): void => {
    clearHostOnly(key);
    document.cookie = `${key}=;${attributes(sharedDomain(), 0)}`;
  },
};

export const siteStorage = {
  getItem: (key: string): Promise<string | null> => {
    try {
      // The localStorage fallback keeps a list saved there before the cookie resumable on its own
      // origin.
      return Promise.resolve(siteCookie.read(key) ?? window.localStorage.getItem(key));
    } catch {
      return Promise.resolve(null);
    }
  },
  setItem: (key: string, value: string): Promise<void> => {
    try {
      siteCookie.write(key, value, PULL_SHEET_MAX_AGE_SECONDS);
    } catch {
      // Storage was refused. The list loses resumability, which is acceptable; it must not crash.
    }
    return Promise.resolve();
  },
  removeItem: (key: string): Promise<void> => {
    try {
      siteCookie.clear(key);
      window.localStorage.removeItem(key);
    } catch {
      // Storage was refused; see `setItem`.
    }
    return Promise.resolve();
  },
};
